import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { useContent, useContactInfo } from "../../hooks/useData";

const Hero = () => {
  const content = useContent();
  const contact = useContactInfo();
  const heroContent = content.hero;
  const screenshots = [
    {
      src: "/images/screenshots/commission/1.webp",
      alt: "Commission Software",
    },
    { src: "/images/system-care-dashboard.webp", alt: "System Care Pro 2026" },
    { src: "/images/screenshots/petrol/1.webp", alt: "Petrol Pump Software" },
  ];

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Hello! I'm interested in your software solutions.",
    );
    window.open(`https://wa.me/${contact.whatsapp}?text=${message}`, "_blank");
  };

  return (
    <>
      <Helmet>
        {/* Preload Critical Hero Images directly where needed */}
        {screenshots.map((s, idx) => (
          <link key={idx} rel="preload" as="image" href={s.src} />
        ))}
      </Helmet>
      <section className="min-h-[90vh] flex items-center relative overflow-hidden bg-gradient-to-br from-primary to-secondary py-8 px-4 mt-[70px]">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center w-full">
          {/* Content Side */}
          <div className="z-10 text-white text-center lg:text-left">
            <h1 className="text-4xl lg:text-6xl font-extrabold mb-4 leading-tight">
              {heroContent.title}
            </h1>
            <p className="text-2xl font-semibold mb-6 text-accent">
              {heroContent.tagline}
            </p>
            <p className="text-lg lg:text-xl leading-relaxed mb-10 opacity-95 max-w-[600px] mx-auto lg:mx-0">
              {heroContent.description}
            </p>
            <div className="flex flex-row items-center justify-center lg:justify-start gap-6">
              <button
                className="inline-flex items-center gap-3 px-10 py-4 bg-primary text-white rounded-full text-lg font-bold shadow-lg shadow-primary/40 transition-all duration-300 hover:bg-secondary hover:-translate-y-0.5 hover:shadow-xl"
                onClick={handleWhatsAppClick}
              >
                <FaWhatsapp className="text-2xl" />
                {heroContent.cta.text}
              </button>
            </div>
          </div>

          {/* Visual Side */}
          <motion.div
            className="relative h-[300px] lg:h-[500px] flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="relative w-full h-full">
              {/* Background Glow - Optimized for mobile (hidden on small screens) */}
              <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4/5 h-4/5 bg-radial-gradient from-accent/40 to-transparent -z-10 blur-[40px]" />

              {screenshots.map((shot, index) => {
                // Custom positioning for distinct cards
                const positions = [
                  "top-[10%] left-0 w-[65%] z-20", // Card 1
                  "bottom-[5%] right-0 w-[60%] z-30", // Card 2
                  "top-[40%] left-[40%] w-[45%] z-40 shadow-2xl", // Card 3
                ];

                return (
                  <motion.div
                    key={index}
                    className={`absolute rounded-xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/10 ${positions[index]}`}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{
                      opacity: 1,
                      y: [0, -15, 0], // Float animation
                    }}
                    transition={{
                      opacity: { delay: 0.5 + index * 0.2, duration: 0.5 },
                      y: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 1,
                      },
                    }}
                    whileHover={{ scale: 1.05, zIndex: 50 }}
                  >
                    <img
                      src={shot.src}
                      alt={shot.alt}
                      className="w-full h-full object-cover block"
                      loading="eager"
                      fetchPriority="high"
                      width="400"
                      height="300"
                    />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Hero;
